import React, { useState, useEffect } from "react";
import axios from "axios"
import Spinner from "../presentation/spinner";
import Button from "@material-ui/core/Button"
import * as styled from "./styled"

export default ({ id }) => {
  const [error, setError] = useState();
  const [projects, setProjects] = useState(null);
  const [updatedValues, setUpdatedValues] = useState({})
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    const loadProjectsFromServer = async (accountId) => {
      const accessToken = localStorage.getItem("idToken");
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`/service/${accountId}/projects`, {
        headers,
      });
      setProjects(response.data)
    };
    if (!id) {
      setError("Invalid URL. AccountId not found");
      return;
    }
    if(!isUpdating){
      loadProjectsFromServer(id);
    }
  }, [id, isUpdating]);

  // valueType can be activeTtl, finalTtl or isUpdated
  const getValueForProject = (project, valueType) => {
    const ALLOWED_VALUES = ['activeTtl', 'finalTtl', 'isUpdated', 'hasSavedValues']
    if(!ALLOWED_VALUES.includes(valueType)){
      throw new Error(`Invalid valueType ${valueType}`)
    }
    const updated = updatedValues[project._id]
    if(valueType === 'isUpdated'){
      return Boolean(updated)
    }
    if(updated && updated.hasOwnProperty(valueType)){
      return updated[valueType]
    }
    if(valueType === 'hasSavedValues'){
      return Boolean(project?.hello?.activeTtl || project?.hello?.finalTtl)
    }
    const targetValue = project?.hello && project.hello[valueType]

    return targetValue ? targetValue / 60 / 60 : "";
  }

  const onUpdateValue = (updateEvent, valueType, projectId) => {
    const ALLOWED_VALUES = ['activeTtl', 'finalTtl']

    if(!ALLOWED_VALUES.includes(valueType)){
      throw new Error(`Invalid valueType ${valueType} for onUpdateValue function`)
    }
    let updatedValue = updatedValues[projectId] || {}
    const MAXIMUM_VALUE = 24 * 14 // 2 weeks maximum
    if(updateEvent.target.value && Number.parseInt(updateEvent.target.value) > MAXIMUM_VALUE){
      return
    }
    updatedValue[valueType] = updateEvent.target.value
    setUpdatedValues(prevValue => {
      const newValue = {
        ...prevValue
      }
      newValue[projectId] = updatedValue
      return newValue
    })
  }

  const onDeleteTtl = async (projectId) => {
    setIsUpdating(true)
    const accessToken = localStorage.getItem("idToken");
    const headers = { Authorization: `Bearer ${accessToken}` };
    try{
      await axios.post(`/service/updateHelloTtl/${projectId}`, {}, {
        headers,
      });
      setUpdatedValues(prevValue => {
        const newValue = {
          ...prevValue
        }
        delete newValue[projectId]
        return newValue
      })
      setProjects(null)
      alert(`Hello TTL Values for project ${projectId} successfully deleted`)
    }catch(err){
      alert('Failed to delete ttl values')
    }
    setIsUpdating(false)
  }

  const onUpdateProjectTtl = async (project) => {
    setIsUpdating(true)
    const accessToken = localStorage.getItem("idToken");
    const headers = { Authorization: `Bearer ${accessToken}` };
    const requestBody = {}
    const activeTtl = getValueForProject(project, 'activeTtl')
    if(activeTtl){
      requestBody.activeTtl = Number.parseInt(activeTtl) * 60 * 60 // Convert hours to seconds
    }
    const finalTtl = getValueForProject(project, 'finalTtl')
    if(finalTtl){
      requestBody.finalTtl = Number.parseInt(finalTtl) * 60 * 60 // Convert hours to seconds
    }
    try{
      await axios.post(`/service/updateHelloTtl/${project._id}`, requestBody, {
        headers,
      });
      setUpdatedValues(prevValue => {
        const newValue = {
          ...prevValue
        }
        delete newValue[project._id]
        return newValue
      })
      setProjects(null)
      alert(`Hello TTL Values for project ${project.name} successfully updated`)
    }catch(err){
      const errorMessage = err.response?.data?.errorMessage || 'Failed to update ttl values'
      alert(errorMessage)
    }
    setIsUpdating(false)
  }

  const renderProjectRows = (projects, error) => {
    if (error) {
      return (
          <styled.ProjectListMessage>
            <h2>{error}</h2>
          </styled.ProjectListMessage>
      );
    }
    if (!projects) {
      return (
        <styled.ProjectListMessage>
          <Spinner size="150" />
          <h2>Retrieving Projects</h2>
        </styled.ProjectListMessage>
      );
    }
    if (projects.length) {
      return (
        <table className="customer-projects-table">
          <thead>
            <tr>
              <styled.HelloTtlConfigColumnHeader>Name</styled.HelloTtlConfigColumnHeader>
              <styled.HelloTtlConfigColumnHeader>Active TTL</styled.HelloTtlConfigColumnHeader>
              <styled.HelloTtlConfigColumnHeader>Final TTL</styled.HelloTtlConfigColumnHeader>
              <styled.HelloTtlConfigColumnHeader>Update</styled.HelloTtlConfigColumnHeader>
              <styled.HelloTtlConfigColumnHeader>Delete</styled.HelloTtlConfigColumnHeader>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project._id}>
                <styled.HelloTtlConfigColumn>{project.name}</styled.HelloTtlConfigColumn>
                <styled.HelloTtlConfigColumn>
                  <input type="number" value={getValueForProject(project, 'activeTtl')} placeholder="activeTtl" onChange={(event) => onUpdateValue(event, 'activeTtl', project._id)}/>
                </styled.HelloTtlConfigColumn>
                <styled.HelloTtlConfigColumn>
                  <input type="number" value={getValueForProject(project, 'finalTtl')} placeholder="finalTtl" onChange={(event) => onUpdateValue(event, 'finalTtl', project._id)}/>
                </styled.HelloTtlConfigColumn>
                <styled.HelloTtlConfigColumn>
                  <Button color="primary" variant="contained" disabled={!getValueForProject(project, 'isUpdated') || isUpdating} onClick={() => onUpdateProjectTtl(project)}>Update</Button>
                </styled.HelloTtlConfigColumn>
                <styled.HelloTtlConfigColumn>
                  <Button color="secondary" variant="contained" disabled={!getValueForProject(project, 'hasSavedValues') || isUpdating} onClick={() => onDeleteTtl(project._id)}>Delete</Button>
                </styled.HelloTtlConfigColumn>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (projects.length === 0) {
      return <h2>No projects found for this account</h2>;
    }
  };

  return (
    <styled.ProjectListExplanation>
      <label className="box-label">
        Configure Hello Order TTL Settings
      </label>
        <p>Hello Screens allows for configuration of the Time To Live for Orders in Hello Screens. The time to live refers to the amount of time the order will
        stay in the Hello DB after it&apos;s last update, and therefore how long the order is visible in Hello Screens. The default value for both attributes is 24 hours.</p>
        <p>
          As Hello Orders usually contain PII the customer's policy when it comes to how we store PII may come in to play when adjusting these values. We have set up two different ttl values, <b>activeTtl</b> and <b>finalTtl</b>.
          The maximum value for both attributes is 336 hours, which is equivalent to 2 weeks.
        </p>
          <p>
            <b>finalTtl</b> refers to orders in the final stage of their life, in general this
            can be understood as Completed orders. The technical definition is orders in a state that has a progression level greater than 94. These orders have a different ttl
            value to other orders as we expect most customers do not need an order to last for a long time after it has reached the completed stage, as generally this is the last interaction
            an order received
          </p>
      <p>
        <b>activeTtl</b> refers to orders in any other state than the finalTtl states, from Registered all the way to states with a progressionLevel of 94. These states indicate
        the order is still active. Some customers may wish to extend this value beyond the default 24 hours, for example if they receive orders well in advance of completion for example
        if a pick up order is placed earlier in the week for a certain day. In these situations it is advised to increase the activeTtl value but leave finalTtl at 24 hours.
      </p>
      {renderProjectRows(projects, error)}
    </styled.ProjectListExplanation>
  )
};
